// Fractional indexing — TS port of core/src/ranking.rs. Same alphabet,
// same algorithm. Keep these two in sync.

export const MAX_RANK_LEN = 64;

const ALPHABET =
  "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
const FIRST = "0".charCodeAt(0);
const LAST = "z".charCodeAt(0);

const BASE62_RE = /^[0-9A-Za-z]+$/;

export function isValidRank(s: string): boolean {
  return s.length > 0 && s.length <= MAX_RANK_LEN && BASE62_RE.test(s);
}

/** Return a rank `m` such that `a < m < b`. `null` means "no bound". */
export function generateKeyBetween(
  a: string | null,
  b: string | null,
): string {
  if (a !== null && (a.length === 0 || !BASE62_RE.test(a))) {
    throw new Error(`bad bound a: ${a}`);
  }
  if (b !== null && (b.length === 0 || !BASE62_RE.test(b))) {
    throw new Error(`bad bound b: ${b}`);
  }
  if (a !== null && b !== null && a >= b) {
    throw new Error(`bounds not ordered: ${a} >= ${b}`);
  }
  if (a === null && b === null) return midpoint("0", "z");
  if (a === null) return justBefore(b!);
  if (b === null) return justAfter(a);
  const m = midpoint(a, b);
  if (m.length > MAX_RANK_LEN) throw new Error("rank too long");
  return m;
}

export function generateNKeysBetween(
  a: string | null,
  b: string | null,
  n: number,
): string[] {
  if (n <= 0) return [];
  if (n === 1) return [generateKeyBetween(a, b)];
  const midIdx = Math.floor(n / 2);
  const mid = generateKeyBetween(a, b);
  const left = generateNKeysBetween(a, mid, midIdx);
  const right = generateNKeysBetween(mid, b, n - midIdx - 1);
  return [...left, mid, ...right];
}

function justBefore(b: string): string {
  for (let i = 0; i < b.length; i++) {
    const c = b.charCodeAt(i);
    if (c > FIRST) {
      const out = b.slice(0, i) + String.fromCharCode(midpointByte(FIRST, c));
      if (out.length > MAX_RANK_LEN) throw new Error("rank too long");
      return out;
    }
  }
  const out = b + String.fromCharCode(midpointByte(FIRST, LAST));
  if (out.length > MAX_RANK_LEN) throw new Error("rank too long");
  return out;
}

function justAfter(a: string): string {
  for (let i = a.length - 1; i >= 0; i--) {
    const c = a.charCodeAt(i);
    if (c < LAST) {
      const next = midpointByte(c, LAST);
      if (next === c) {
        const out = a + String.fromCharCode(midpointByte(FIRST, LAST));
        if (out.length > MAX_RANK_LEN) throw new Error("rank too long");
        return out;
      }
      const out = a.slice(0, i) + String.fromCharCode(next);
      if (out.length > MAX_RANK_LEN) throw new Error("rank too long");
      return out;
    }
  }
  const out = a + String.fromCharCode(midpointByte(FIRST, LAST));
  if (out.length > MAX_RANK_LEN) throw new Error("rank too long");
  return out;
}

function midpoint(a: string, b: string): string {
  let common = 0;
  while (
    common < a.length &&
    common < b.length &&
    a.charCodeAt(common) === b.charCodeAt(common)
  ) {
    common++;
  }
  const prefix = a.slice(0, common);
  const aByte = common < a.length ? a.charCodeAt(common) : FIRST - 1;
  const bByte = common < b.length ? b.charCodeAt(common) : LAST + 1;

  if (bByte > aByte + 1) {
    return prefix + String.fromCharCode(midpointByte(aByte, bByte));
  }
  if (common < a.length) {
    let out = prefix + String.fromCharCode(a.charCodeAt(common));
    let i = common + 1;
    while (i < a.length && a.charCodeAt(i) === LAST) {
      out += "z";
      i++;
    }
    const cur = i < a.length ? a.charCodeAt(i) : FIRST - 1;
    out += String.fromCharCode(midpointByte(cur, LAST + 1));
    return out;
  }
  return prefix + String.fromCharCode(midpointByte(FIRST - 1, bByte));
}

function midpointByte(lo: number, hi: number): number {
  const l = byteToPos(lo);
  const h = byteToPos(hi);
  const mid = Math.floor((l + h) / 2);
  return posToByte(mid);
}

function byteToPos(b: number): number {
  if (b < FIRST) return -1;
  for (let i = 0; i < ALPHABET.length; i++) {
    if (ALPHABET.charCodeAt(i) === b) return i;
  }
  return ALPHABET.length; // one past LAST
}

function posToByte(p: number): number {
  if (p < 0) return FIRST;
  if (p >= ALPHABET.length) return LAST;
  return ALPHABET.charCodeAt(p);
}
